import { useId, type ReactNode } from 'react';
import { Form } from 'react-bootstrap';

export interface EditRadioGroupOption<V> {
  readonly key: string | number;
  readonly value: V;
  readonly label: string;
  /**
   * Optional content rendered immediately below the radio. Wrapped in
   * `<fieldset disabled>` whenever this option is NOT the current
   * selection — HTML's native form-control disabling propagates to
   * any `<input>` / `<select>` / `<textarea>` / `<button>` inside, so
   * the wrapped form controls don't need their own `disabled` prop.
   */
  readonly content?: ReactNode;
}

interface EditRadioGroupProps<V> {
  /**
   * Optional group label. When provided, the radios are wrapped in a
   * bordered container with the label cutting the top border (classic
   * fieldset/legend look); `mandatory` then appends " *" to it. When
   * omitted, the radios render bare — no border, no label, and
   * `mandatory` has no effect.
   */
  readonly label?: string;
  readonly value: V;
  readonly onChange: (value: V) => void;
  readonly mandatory?: boolean;
  readonly options: readonly EditRadioGroupOption<V>[];
}

/**
 * Stacked radio group with each radio's associated content rendered
 * directly underneath. The content for the non-selected radios stays
 * visible but disabled — values typed there persist across mode
 * switches (so toggling and toggling back doesn't blow away input).
 */
export const EditRadioGroup = <V,>({ label, value, onChange, mandatory = false, options }: EditRadioGroupProps<V>) => {
  const groupId = useId();
  const radios = options.map((opt) => {
    const selected = value === opt.value;
    return (
      <div key={opt.key} className="mb-2">
        <Form.Check type="radio" id={`${groupId}-${opt.key}`} name={groupId} label={opt.label} checked={selected} onChange={() => onChange(opt.value)} />
        {opt.content !== undefined && (
          <fieldset disabled={!selected} className="ms-4 mt-2">
            {opt.content}
          </fieldset>
        )}
      </div>
    );
  });

  if (label === undefined) {
    return <>{radios}</>;
  }

  return (
    <div className="border rounded p-3 position-relative">
      <Form.Label className="position-absolute bg-white px-2 mb-0" style={{ top: '-0.35rem', left: '0.5rem' }}>
        {mandatory ? `${label} *` : label}
      </Form.Label>
      {radios}
    </div>
  );
};
